import React from "react";

interface UnderlineProps {
  active: boolean;
  children: React.ReactNode;
}

type Props = {
  mapColor: string;
  displayValue: number;

  sexString: string;
  ageString: string;
  insuranceString: string;
  cohortString: string;
  stateString: string;
  yearString: string;
  popUpString: string;

  avgValue: string;
  decimalPoint: number;

  convertGenderString: (s: string) => string;
  formatNumberWithCommas: (n: number) => string | number;
};

const USAvgText: React.FC<Props> = ({
  mapColor,
  displayValue,
  sexString,
  ageString,
  insuranceString,
  cohortString,
  stateString,
  yearString,
  popUpString,
  avgValue,
  decimalPoint,
  convertGenderString,
  formatNumberWithCommas,
}) => {
  if (!mapColor) return null;

  const avgText = avgValue
    ? formatNumberWithCommas(Number(Number(avgValue).toFixed(decimalPoint)))
    : "";

  const isDefaultCohort = cohortString === "HCM";
  const isDefaultAge = ageString === "of all ages";
  const isDefaultSex = sexString === "females and males";
  const isDefaultInsurance = insuranceString === " all insurance types";
  const isDefaultState = stateString === "the US";
  const isDefaultYear = yearString === " 2016-2026";

  const U: React.FC<UnderlineProps> = ({active, children}) => (
    <span className={active ? "italic whitespace-nowrap" : "whitespace-nowrap"}>
      {children}
    </span>
  );

  const formatAge = (age: string, active: boolean) => {
    if (!age.startsWith("of ")) {
      return <U active={active}>{age}</U>;
    }

    const rest = age.slice(3);

    return (
      <>
        of <U active={active}>{rest}</U>
      </>
    );
  };

  return (
    <div
      style={{backgroundColor: mapColor}}
      className="rounded-2 text-white text-[16px] xl:text-[22px] p-4 rounded-[8px] xl:rounded-[16px] h-fit shadow-md font-medium w-[95%] mx-auto xl:w-[100%] flex items-center justify-center">
      {displayValue < 2 ? (
        <div>
          Among <U active={!isDefaultSex}>{convertGenderString(sexString)}</U>{" "}
          {formatAge(ageString, !isDefaultAge)} with{" "}
          <U active={!isDefaultInsurance}>{insuranceString}</U>, there were{" "}
          <b>{avgText}</b> {popUpString}{" "}
          <U active={!isDefaultCohort}>{cohortString}</U> cases per 100,000
          persons in <U active={!isDefaultState}>{stateString}</U> in{" "}
          <U active={!isDefaultYear}>{yearString}</U>.
        </div>
      ) : displayValue < 4 ? (
        <div>
          Among <U active={!isDefaultSex}>{convertGenderString(sexString)}</U>{" "}
          {formatAge(ageString, !isDefaultAge)} with{" "}
          <U active={!isDefaultInsurance}>{insuranceString}</U>, there were an
          estimated <b>{avgText}</b> {popUpString}{" "}
          <U active={!isDefaultCohort}>{cohortString}</U> cases in{" "}
          <U active={!isDefaultState}>{stateString}</U> in{" "}
          <U active={!isDefaultYear}>{yearString}</U>.
        </div>
      ) : displayValue < 48 ? (
        <div>
          Among <U active={!isDefaultSex}>{sexString}</U> prevalent{" "}
          <U active={!isDefaultCohort}>{cohortString}</U> cases{" "}
          {formatAge(ageString, !isDefaultAge)} with{" "}
          <U active={!isDefaultInsurance}>{insuranceString}</U>,{" "}
          <b>{avgText}%</b> {popUpString} in{" "}
          <U active={!isDefaultState}>{stateString}</U> in{" "}
          <U active={!isDefaultYear}>{yearString}</U>.
        </div>
      ) : displayValue < 54 ? (
        <div>
          Among <U active={!isDefaultSex}>{sexString}</U>{" "}
          <U active={!isDefaultCohort}>{cohortString}</U> cases{" "}
          {formatAge(ageString, !isDefaultAge)} with{" "}
          <U active={!isDefaultInsurance}>{insuranceString}</U> that underwent
          SRT in <U active={!isDefaultYear}>{yearString}</U>, <b>{avgText}%</b>{" "}
          {popUpString} in <U active={!isDefaultState}>{stateString}</U>.
        </div>
      ) : displayValue < 66 ? (
        <div>
          Among <U active={!isDefaultSex}>{sexString}</U> prevalent{" "}
          <U active={!isDefaultCohort}>{cohortString}</U> cases{" "}
          {formatAge(ageString, !isDefaultAge)} with{" "}
          <U active={!isDefaultInsurance}>{insuranceString}</U>, {popUpString}{" "}
          <b>${avgText}</b> in <U active={!isDefaultState}>{stateString}</U> in{" "}
          <U active={!isDefaultYear}>{yearString}</U>.
        </div>
      ) : displayValue < 76 ? (
        <div>
          Among <U active={!isDefaultSex}>{sexString}</U> prevalent{" "}
          <U active={!isDefaultCohort}>{cohortString}</U> cases{" "}
          {formatAge(ageString, !isDefaultAge)} with{" "}
          <U active={!isDefaultInsurance}>{insuranceString}</U>,{" "}
          <b>{avgText}%</b> {popUpString} in{" "}
          <U active={!isDefaultState}>{stateString}</U> in{" "}
          <U active={!isDefaultYear}>{yearString}</U>.
        </div>
      ) : displayValue === 76 ? (
        <div>
          <b>{avgText}%</b>{" "}
          <U active={!isDefaultSex}>{convertGenderString(sexString)}</U>{" "}
          {formatAge(ageString, !isDefaultAge)} were living in poverty in{" "}
          <U active={!isDefaultState}>{stateString}</U> in{" "}
          <U active={!isDefaultYear}>{yearString}</U>.
        </div>
      ) : displayValue === 77 ? (
        <div>
          <b>{avgText}%</b> of{" "}
          <U active={!isDefaultSex}>{convertGenderString(sexString)}</U>{" "}
          {formatAge(ageString, !isDefaultAge)} were uninsured in{" "}
          <U active={!isDefaultState}>{stateString}</U> in{" "}
          <U active={!isDefaultYear}>{yearString}</U>.
        </div>
      ) : displayValue < 81 ? (
        <div>
          Among <U active={!isDefaultSex}>{sexString}</U> prevalent{" "}
          <U active={!isDefaultCohort}>{cohortString}</U> cases{" "}
          {formatAge(ageString, !isDefaultAge)} with{" "}
          <U active={!isDefaultInsurance}>{insuranceString}</U>,{" "}
          <b>{avgText}%</b> {popUpString} in{" "}
          <U active={!isDefaultState}>{stateString}</U> in{" "}
          <U active={!isDefaultYear}>{yearString}</U>.
        </div>
      ) : (
        <div>
          <b>{avgText}%</b> of{" "}
          <U active={!isDefaultSex}>{convertGenderString(sexString)}</U>{" "}
          {formatAge(ageString, !isDefaultAge)} {popUpString} in{" "}
          <U active={!isDefaultState}>{stateString}</U> in{" "}
          <U active={!isDefaultYear}>{yearString}</U>.
        </div>
      )}
    </div>
  );
};

export default USAvgText;
